import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useReferenceObjectAdmin } from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignCapability, HelpLink, Json } from '../../../../support/campaigns';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import LinkListEditor from './LinkListEditor';
import { ReferenceSelect } from './ReferenceSelect';
import { useGlobalReferenceOptions } from './builderGlobalReferences';
import { byOrder, nextOrderOf, signature } from './builderShared';

/** Metadata sub-key holding a capability's OPTIONAL cross-links to canonical toolbox records. */
const LINKS_KEY = 'links';
type CapabilityLinks = { capability?: string; product?: string; goToMarket?: string };

function readLinks(meta: Json | undefined): CapabilityLinks {
  const links = (meta as { [LINKS_KEY]?: unknown } | undefined)?.[LINKS_KEY];
  return links && typeof links === 'object' ? (links as CapabilityLinks) : {};
}

/** Merge a single link value into the row's metadata, returning the full new metadata object. */
function withLink(meta: Json | undefined, key: keyof CapabilityLinks, value: string): Json {
  const base = (meta && typeof meta === 'object' ? meta : {}) as Record<string, unknown>;
  const links = { ...readLinks(meta) };
  if (value) links[key] = value;
  else delete links[key];
  return { ...base, [LINKS_KEY]: links } as Json;
}

/**
 * Admin editor for a campaign's capability/play catalogue (`CampaignCapability`) — the single
 * de-duplicated source of truth that content blocks, questions and scenarios reference by `key`.
 *
 * Rows are drag-orderable (the drag rank persists to `order`) and each carries a stable `key`, a
 * `name`, short `description`, a rich `detail` body, a report-ready `proposal`, and an optional list of
 * `{ label, url }` resource links (authored with the shared `LinkListEditor`). Persistence mirrors the
 * rest of the builder: rows are created immediately on "Add" (so they get an id to drag against), text
 * edits flush on blur, and toggles/links flush on blur/change — every write is the full-record PUT the
 * Go runtime requires (`{ ...row, ...changes }`), never a delta.
 */

export function CapabilitiesEditor(props: { campaign: Campaign | null; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  // Global (library) mode = no campaign: manage every global capability directly (create/edit/delete).
  // Campaign mode = this campaign references a subset. See PersonasEditor for the full dual-mode pattern
  // (linked/unlinked/detach vs all/create/removeGlobal).
  const global = campaign == null;
  const admin = useReferenceObjectAdmin<CampaignCapability>('capability', campaign?.id);
  const rows = global ? admin.all : admin.linked;
  const isLoading = admin.isLoading;
  const mut = admin;

  const [attachAnchor, setAttachAnchor] = React.useState<null | HTMLElement>(null);

  // Options for the optional cross-links (existing SAFE capability / product / go-to-market records),
  // fetched once for the whole catalogue rather than per row.
  const { capabilityOptions, productOptions, goToMarketOptions } = useGlobalReferenceOptions();

  // Local mirror so typing stays responsive between refetches; reseeded when the server list changes.
  const [caps, setCaps] = React.useState<CampaignCapability[]>([]);
  const sig = signature(rows);
  React.useEffect(() => {
    setCaps(byOrder(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Rows are collapsed by default (a dense one-line summary); expanding one reveals its edit form.
  const [expanded, setExpanded] = React.useState<Set<number>>(() => new Set());
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const patchLocal = (id: number, next: Partial<CampaignCapability>) =>
    setCaps((prev) => prev.map((c) => (c.id === id ? { ...c, ...next } : c)));

  const save = (row: CampaignCapability, next: Partial<CampaignCapability>) => {
    patchLocal(row.id, next);
    mut.update.mutate({ ...row, ...next, id: row.id });
  };

  const add = () => {
    const fields = {
      key: '',
      name: '',
      description: '',
      detail: '',
      proposal: '',
      resource_links: [],
      order: nextOrderOf(caps),
      hidden: false,
      metadata: {},
    };
    // Global mode creates a bare global capability; campaign mode creates-and-links it.
    void (global ? admin.create(fields) : admin.createLinked(fields))
      // Auto-expand the freshly created row so it's ready to edit.
      .then((created) => {
        if (created && typeof created.id === 'number') {
          setExpanded((prev) => new Set(prev).add(created.id));
        }
      })
      .catch(() => undefined);
  };

  const attach = (id: number) => admin.attachExisting(id);

  // Campaign mode: "Remove" detaches this campaign's reference; the global capability (which another
  // campaign may reference by the same key) is left intact. Global mode: delete the global capability
  // everywhere, with confirmation.
  const remove = (row: CampaignCapability) => {
    if (global) {
      const ok = window.confirm(
        `Delete “${row.name || row.key || 'this capability'}” from the global library? It will be removed from every campaign that references it.`,
      );
      if (!ok) return;
      setCaps((prev) => prev.filter((c) => c.id !== row.id));
      admin.removeGlobal(row.id);
      return;
    }
    setCaps((prev) => prev.filter((c) => c.id !== row.id));
    admin.detach(row.id);
  };

  // Persist a new drag order: PUT every capability whose position changed (full record + new `order`).
  const reorder = (nextList: CampaignCapability[]) => {
    setCaps(nextList);
    nextList.forEach((row, index) => {
      if ((row.order ?? 0) !== index) mut.update.mutate({ ...row, order: index, id: row.id });
    });
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Capabilities & plays"
        subtitle="The de-duplicated catalogue of capabilities/plays this campaign references. Content blocks, questions and scenarios point at these by key — edit the prose once here. Drag to order."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {caps.length === 0 && (
            <Box sx={{ color: 'text.secondary', mb: 1, fontSize: 14 }}>
              No capabilities yet. Add the plays/products this campaign can propose.
            </Box>
          )}

          <ReactSortable
            list={caps.map((c) => ({ ...c }))}
            setList={(next) => canWrite && reorder(next as CampaignCapability[])}
            handle=".cb-cap-drag-handle"
            animation={150}
            disabled={!canWrite}
          >
            {caps.map((row) => (
              <Accordion
                key={row.id}
                disableGutters
                expanded={expanded.has(row.id)}
                onChange={() => toggle(row.id)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: 'none',
                  opacity: row.hidden ? 0.55 : 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  // component="div" so the summary is not a real <button>: it hosts the hide/remove
                  // IconButtons, and a <button> inside a <button> is invalid DOM (React warns). Click
                  // still toggles via Accordion's onChange, and ButtonBase keeps role="button".
                  component="div"
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5, minWidth: 0 } }}
                >
                  <Box
                    className="cb-cap-drag-handle"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ cursor: canWrite ? 'grab' : 'default', color: 'text.disabled', display: 'flex' }}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </Box>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0, flexShrink: 1 }}>
                    {row.name || row.key || '(unnamed capability)'}
                  </Typography>
                  {row.key && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>
                      {row.key}
                    </Typography>
                  )}
                  {(row.resource_links?.length ?? 0) > 0 && (
                    <Chip size="small" variant="outlined" label={`${row.resource_links?.length} links`} />
                  )}
                  {global && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`used by ${admin.usageCount(row.id)} campaign${admin.usageCount(row.id) === 1 ? '' : 's'}`}
                    />
                  )}
                  {row.hidden && (
                    <Chip size="small" variant="outlined" icon={<VisibilityOffIcon fontSize="small" />} label="Hidden" />
                  )}
                  {canWrite && (
                    <Stack direction="row" sx={{ ml: 'auto', pl: 1 }}>
                      <Tooltip title={row.hidden ? 'Hidden — show' : 'Hide from the catalogue'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            save(row, { hidden: !row.hidden });
                          }}
                        >
                          {row.hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={global ? 'Delete capability (removes it from all campaigns)' : 'Remove capability'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(row);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Key"
                        size="small"
                        value={row.key}
                        disabled={!canWrite}
                        helperText="Stable id referenced by other rows"
                        onChange={(e) => patchLocal(row.id, { key: e.target.value })}
                        onBlur={() => save(row, { key: row.key })}
                        sx={{ minWidth: 200 }}
                      />
                      <TextField
                        label="Name"
                        size="small"
                        fullWidth
                        value={row.name}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { name: e.target.value })}
                        onBlur={() => save(row, { name: row.name })}
                      />
                    </Stack>
                    <TextField
                      label="Description"
                      size="small"
                      fullWidth
                      multiline
                      value={row.description}
                      disabled={!canWrite}
                      onChange={(e) => patchLocal(row.id, { description: e.target.value })}
                      onBlur={() => save(row, { description: row.description })}
                    />
                    <TextField
                      label="Detail (rich / HTML)"
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      value={row.detail}
                      disabled={!canWrite}
                      helperText="Long-form body; HTML is allowed"
                      onChange={(e) => patchLocal(row.id, { detail: e.target.value })}
                      onBlur={() => save(row, { detail: row.detail })}
                    />
                    <TextField
                      label="Proposal"
                      size="small"
                      fullWidth
                      multiline
                      value={row.proposal}
                      disabled={!canWrite}
                      helperText="Report-ready recommendation text used when this capability is proposed"
                      onChange={(e) => patchLocal(row.id, { proposal: e.target.value })}
                      onBlur={() => save(row, { proposal: row.proposal })}
                    />
                    <Box onBlur={() => save(row, { resource_links: row.resource_links })}>
                      <LinkListEditor
                        value={(row.resource_links ?? []) as HelpLink[]}
                        onChange={(links) => patchLocal(row.id, { resource_links: links })}
                        disabled={!canWrite}
                        help="Docs, decks, or references surfaced alongside this capability."
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                      Linked records (optional) — tie this play back to the canonical catalogue record it
                      maps to.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <ReferenceSelect
                        label="DB capability"
                        value={readLinks(row.metadata).capability ?? ''}
                        options={capabilityOptions}
                        emptyLabel="None"
                        disabled={!canWrite}
                        fullWidth
                        onChange={(v) => save(row, { metadata: withLink(row.metadata, 'capability', v) })}
                      />
                      <ReferenceSelect
                        label="Product"
                        value={readLinks(row.metadata).product ?? ''}
                        options={productOptions}
                        emptyLabel="None"
                        disabled={!canWrite}
                        fullWidth
                        onChange={(v) => save(row, { metadata: withLink(row.metadata, 'product', v) })}
                      />
                      <ReferenceSelect
                        label="Go-to-market"
                        value={readLinks(row.metadata).goToMarket ?? ''}
                        options={goToMarketOptions}
                        emptyLabel="None"
                        disabled={!canWrite}
                        fullWidth
                        onChange={(v) => save(row, { metadata: withLink(row.metadata, 'goToMarket', v) })}
                      />
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </ReactSortable>

          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <GhostButton onClick={add} disabled={mut.isSaving}>
                + Add capability
              </GhostButton>
              {/* "Attach existing" only makes sense inside a campaign; the global library shows all. */}
              {!global && (
                <>
                  <GhostButton
                    onClick={(e) => setAttachAnchor(e.currentTarget)}
                    disabled={mut.isSaving || admin.unlinked.length === 0}
                  >
                    Add existing
                  </GhostButton>
                  <Menu
                    anchorEl={attachAnchor}
                    open={Boolean(attachAnchor)}
                    onClose={() => setAttachAnchor(null)}
                  >
                    {admin.unlinked.map((c) => (
                      <MenuItem
                        key={c.id}
                        onClick={() => {
                          attach(c.id);
                          setAttachAnchor(null);
                        }}
                      >
                        {c.name || c.key || `(unnamed capability #${c.id})`}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          )}
        </>
      )}
    </div>
  );
}

export default CapabilitiesEditor;
